import type { World } from '../sim/types';
import { ECONOMY } from '../sim/economy';
import { ISLAND_NAMES, REEF_ISLANDS, type ReefIsland } from '../sim/reef';
import { RECENT_DAYS, coralHistory, coralVerdict, type CoralVerdict } from './reefHealth';
import { patrolHoursAdded } from '../sim/management';
import { FUNDS_MONTHS, HEAT_ALERT_DHW, MONTH_DAYS, atSea, busyOn, dollars, isCleanupHome, freeLiaison, monthsInHand, protectionJob, recurringPerMonth, surveyCost, volunteersOn } from '../sim/operations';
import { concernsOf } from '../sim/areas';
import { nextGrantDay, runwayWords, yearMoney } from './runway';
import { SEASON, currentObjective } from '../sim/campaign';
import { civilFromEpochDay, dateLabel, epochDayFromCivil, epochDayOf } from '../sim/calendar';
import type { SurveyKind } from '../sim/economy';
import { CLEARED_T_PER_DAY, shoreDebrisT } from '../sim/shoreDebris';

/**
 * The sanctuary's goals (M278, `docs/STRATEGIC_PLAY.md` section 1: "broad objectives ... different actions contribute
 * to the same objective ... a recommended option that does not remove the choice"), in place of one objective at a
 * time once the first chapter's guidance is done. Each is read from the world as it stands, none stored: where it
 * stands, met or not, the way the game recommends now, and the other ways to it. The four the brief names: knowledge of
 * an under-monitored reef, a human pressure eased (the fishing on the grazing fish, the one the model holds by island),
 * the warm season prepared for, and the program kept solvent.
 */
export type GoalId = 'decline' | 'pressure' | 'evaluate' | 'season' | 'money' | 'shore';
export interface Goal {
  id: GoalId;
  title: string;
  /** Where it stands, in a sentence. */
  state: string;
  level: 'met' | 'doing' | 'open';
  /** The way recommended now, a HUD act (`area:<island>`, `respond:<island>` or a page's kind); none where nothing is to be done now (met, or waiting on work under way). */
  way: { label: string; act: string } | null;
  /** The other ways to it, in words. */
  others: string;
  /** What the way recommended costs, and what choosing it gives up (M287, the brief's 7.3: each choice's opportunity cost). */
  cost?: string;
  gives?: string;
}

/**
 * Where the conservancy's liaisons are, when none is free (M311, the fourth playtest's: "my liaison was occupied by that
 * training", the guidance still offering the protection): what the first one free does, the day it is free, and how
 * many are employed; none where a liaison is free now. With none employed, what is told is that one is needed (its
 * review: the island's step still offered the protected area with no liaison at all).
 */
export function liaisonBusy(world: World): { doing: string; until: number | null; count: number } | null {
  const ops = world.operations;
  const liaisons = ops.staff.filter((s) => s.role === 'liaison');
  if (liaisons.length === 0) return { doing: '', until: null, count: 0 };
  if (freeLiaison(ops) !== undefined) return null;
  const doingOf = (j: NonNullable<ReturnType<typeof busyOn>>): string => {
    if (j.kind === 'community' && j.island) return `trains ${ISLAND_NAMES[j.island]}'s volunteers`;
    // The rule's island named, or the limit's kind, never its words (its review: a bag limit's own colon and figure read as part of the sentence).
    if (j.rule?.type === 'protect_herbivores') return `consults ${ISLAND_NAMES[j.rule.island]} on ${j.rule.protect ? 'its protected area' : 'lifting its protected area'}`;
    return j.rule ? 'consults on a fishing limit' : 'is at work';
  };
  const busy = liaisons.flatMap((s) => { const j = busyOn(ops, s.id); return j ? [j] : []; })
    // Free on the day its phase ends, the tick's end of it that morning (measured: 31 January for volunteers begun 1 January).
    .map((j) => ({ until: j.phaseEnds[0] as number, doing: doingOf(j), count: liaisons.length }))
    .sort((a, b) => a.until - b.until);
  return busy[0] ?? null;
}

/** "Your liaison trains Maui's volunteers until 31 January; a protected area waits until then" (M311), or that one is needed. */
export const liaisonWaitWords = (b: { doing: string; until: number | null; count: number }): string => b.count === 0
  ? 'the conservancy employs no liaison; a protected area waits for one hired'
  : b.count > 1 ? `both your liaisons are busy, the first free on ${dateLabel(b.until as number)} (it ${b.doing}); a protected area waits until then`
  : `your liaison ${b.doing} until ${dateLabel(b.until as number)}; a protected area waits until then`;

/** The island Research opens first: one the record flags and the conservancy has not counted (a decline before an unknown), else the least known. */
export function researchIsland(world: World): ReefIsland {
  const flagged = REEF_ISLANDS.map((island) => ({ island, c: concernsOf(world, island), ours: world.operations.areaCounts.some((x) => x.island === island && x.kind === 'cover') }));
  return flagged.find((f) => f.c?.decline && !f.ours)?.island ?? flagged.find((f) => f.c?.uncertain)?.island ?? 'maui';
}

/**
 * The days an island's own count stays news (JUDGED, a game's: a year, so each count reads the reef since its last warm
 * season; the conservancy's own watch, closer than NOAA's surveys, which come every three years, McCoy and Shantz 2026).
 */
const KNOWN_DAYS = RECENT_DAYS;

/**
 * Whether the conservancy counted an island's coral island-wide (every depth, as `coralHistory` reads it: review M287's,
 * a sanctuary-wide survey with no site at a depth had counted), or it or its volunteers its grazing fish, within the year.
 */
const countedLately = (world: World, island: ReefIsland, kind: 'cover' | 'herbivores'): boolean => {
  const today = epochDayOf(world);
  if (kind === 'cover') return coralHistory(world, island).some((p) => p.by === 'conservancy' && p.day > today - KNOWN_DAYS);
  return world.operations.areaCounts.some((c) => c.island === island && c.kind === kind && c.by !== 'noaa' && c.day > today - KNOWN_DAYS);
};

/** Whether a survey of an island (of the kinds given, or any) is at sea or in the plan; one over the whole sanctuary counts every island (review M278's: a survey at sea recommended again). */
const underway = (world: World, island: ReefIsland, kinds?: readonly SurveyKind[]): boolean => {
  const ops = world.operations;
  const fits = (kind: SurveyKind | null, where: ReefIsland | null): boolean => (where === null || where === island) && (!kinds || (kind !== null && kinds.includes(kind)));
  return atSea(ops).some((j) => fits(j.survey, j.island)) || ops.plan.some((p) => fits(p.kind, p.island));
};

/** The coral survey's cost and the boat's days, as a goal's way tells them (the island card's default plan, 10 days and two divers). */
export const SURVEY_DAYS = 10;
const surveyWords = (kind: SurveyKind): string => `${dollars(surveyCost(kind, SURVEY_DAYS))}, a boat ${SURVEY_DAYS} days`;
/** What the boat's days cost: behind the trips already planned it waits its turn (the plan sails first in, first out; M309's review: it had said the planned trips wait), or another island's survey waits if none is. */
const boatTradeoff = (world: World): string => (world.operations.plan.length > 0
  ? `it waits behind the ${world.operations.plan.length} ${world.operations.plan.length === 1 ? 'trip' : 'trips'} already planned`
  : atSea(world.operations).length >= world.operations.boats ? 'it waits for a boat to come home' : 'no other survey can use that boat meanwhile');

/**
 * Whether the coral is still falling where it fell (M287, the brief's "improve monitoring of an important ecological
 * concern", in place of M278's every island counted each year): each island whose measured coral fell past its error
 * (`coralVerdict`), measured again by the conservancy within the year; the largest fall first. A survey is asked only
 * where the record shows a loss whose course since is unknown, never to fill a checklist.
 */
function decline(world: World): Goal {
  // Each island NOAA's record shows fallen, its fall measured on that record (a count of the conservancy's showing no
  // clear change since keeps it on the watch, a loss once measured not forgotten), the largest first.
  const drop = (v: CoralVerdict): number => (v.trend === 'down' && v.against && v.last ? v.against.value - v.last.value : 0);
  const falls = REEF_ISLANDS.map((island) => ({ island, v: coralVerdict(world, island, true) })).filter((x) => x.v.trend === 'down').sort((a, b) => drop(b.v) - drop(a.v));
  const fallen = falls.map((x) => x.island);
  const counted = fallen.filter((island) => countedLately(world, island, 'cover'));
  const going = fallen.filter((island) => !counted.includes(island) && underway(world, island, ['reefBenthic']));
  const target = fallen.find((island) => !counted.includes(island) && !going.includes(island));
  const tv = target ? coralVerdict(world, target, true) : null;
  const names = (xs: readonly ReefIsland[]): string => xs.map((x) => ISLAND_NAMES[x]).join(', ');
  const all = fallen.length > 0 && counted.length === fallen.length;
  return {
    id: 'decline', title: 'Find out whether the coral is still falling',
    state: fallen.length === 0 ? 'No island\'s coral has fallen on the record.'
      : target && tv?.last && tv.against ? `${fellWords(world, target)}${going.length > 0 ? `. Surveys under way: ${names(going)}` : ''}.`
      : all ? `Measured again within the year where it fell: ${names(counted)}.`
      : `Surveys under way: ${names(going)}.`,
    level: fallen.length === 0 || all ? 'met' : counted.length > 0 || going.length > 0 ? 'doing' : 'open',
    way: target ? { label: `Survey ${ISLAND_NAMES[target]}'s coral`, act: `area:${target}:reefBenthic` } : null,
    others: `Only a coral survey measures the coral.${tv?.last ? ` NOAA's divers last surveyed ${ISLAND_NAMES[target as ReefIsland]} in ${tv.last.year}.` : ''}`,
    cost: target ? surveyWords('reefBenthic') : undefined,
    gives: target ? boatTradeoff(world) : undefined,
  };
}

/**
 * Whether the protection works (M287, the brief's "measurable operational or ecological outcomes"): each island whose
 * grazing fish are protected or patrolled, its grazing fish counted by the conservancy or its volunteers within the
 * year, the evidence that would show them recovering. Offered once fishing is eased somewhere.
 */
function evaluate(world: World): Goal | null {
  const m = world.sanctuaries[0]?.management;
  if (!m) return null;
  const eased = REEF_ISLANDS.filter((_, i) => m.herbivoreProtection[i] === true || (m.patrols[i] ?? 0) > 0);
  if (eased.length === 0) return null;
  // Counting, or being set up (review M287's: M278's counted the liaison's setting up as under way).
  const volunteers = (island: ReefIsland): boolean => volunteersOn(world.operations, island);
  const counted = eased.filter((island) => countedLately(world, island, 'herbivores'));
  const going = eased.filter((island) => !counted.includes(island) && (underway(world, island, ['reefFish']) || volunteers(island)));
  const target = eased.find((island) => !counted.includes(island) && !going.includes(island));
  const all = counted.length === eased.length;
  return {
    id: 'evaluate', title: 'Measure whether your protection works',
    state: target ? `${ISLAND_NAMES[target]}'s grazing fish are ${m.herbivoreProtection[REEF_ISLANDS.indexOf(target)] === true ? 'protected' : 'patrolled'} but not counted within the year: without a count, no one can tell whether they are recovering.`
      : all ? 'The grazing fish counted within the year on every island you protect.' : 'Counts under way on every island you protect.',
    level: all ? 'met' : counted.length > 0 || going.length > 0 ? 'doing' : 'open',
    way: target ? { label: `Count ${ISLAND_NAMES[target]}'s grazing fish`, act: `area:${target}:reefFish` } : null,
    others: `Volunteers count an island's fish every month for ${dollars(ECONOMY.community.costPerYear / 12)}, no boat needed. At Kahekili, Maui's herbivore reserve, the bullethead parrotfish took about six years to more than triple (Williams et al. 2016): expect years, not months.`,
    cost: target ? surveyWords('reefFish') : undefined,
    gives: target ? boatTradeoff(world) : undefined,
  };
}

function pressure(world: World): Goal {
  const m = world.sanctuaries[0]?.management;
  const proposed = (island: ReefIsland): boolean => protectionJob(world.operations, island) !== undefined;
  const inForce = (i: number): boolean => m?.herbivoreProtection[i] ?? false;
  const held = REEF_ISLANDS.filter((island, i) => inForce(i) || (m?.patrols[i] ?? 0) > 0);
  const coming = REEF_ISLANDS.filter((island) => proposed(island));
  // The most vulnerable reef first (M287, the brief's "protect a vulnerable reef"): of the islands with no protected
  // area in force or proposed, the one whose coral fell furthest on NOAA's record, then the rest as they stand (review
  // M278's: one already protected was named); none where every island has one.
  const fall = (island: ReefIsland): number => { const v = coralVerdict(world, island, true); return v.trend === 'down' && v.against && v.last ? v.against.value - v.last.value : 0; };
  const free = REEF_ISLANDS.filter((island, i) => !inForce(i) && !proposed(island)).sort((a, b) => fall(b) - fall(a));
  const target = free[0];
  // One stretch of coast's patrol, a month's officers' hours (the island card's own offer).
  const patrolMonth = target ? Math.round(patrolHoursAdded(target, 1) * ECONOMY.officerHour * MONTH_DAYS / 365) : 0;
  const liaisonFree = freeLiaison(world.operations) !== undefined;
  const noLiaison = !world.operations.staff.some((s) => s.role === 'liaison');
  return {
    id: 'pressure', title: 'Ease the fishing on the grazing fish',
    state: held.length > 0 ? `${held.map((i) => ISLAND_NAMES[i]).join(', ')}: a protected area in force or a patrol on the water.`
      : coming.length > 0 ? `A protected area in process for ${coming.map((i) => ISLAND_NAMES[i]).join(', ')}; nothing in force yet.`
      : 'No island\'s grazing fish protected beyond Hawaiʻi\'s own rules.',
    level: held.length > 0 ? 'met' : coming.length > 0 ? 'doing' : 'open',
    // With no liaison free, the patrol is the way (M311: the protected area's press was refused).
    // In debt no patrol can be bought either (its review: `set_patrol` refuses a raise below nothing in hand).
    // A patrol alone only while the funds keep half a year after it (M317, G7).
    way: target && (liaisonFree || patrolAffordable(world, target)) ? { label: liaisonFree ? `Protect ${ISLAND_NAMES[target]}'s grazing fish` : `Patrol ${ISLAND_NAMES[target]}'s reef now`, act: `respond:${target}` } : null,
    cost: target ? (liaisonFree ? `a protected area ${dollars(ECONOMY.rule.consultationCost)} and your liaison ${Math.round(ECONOMY.rule.consultationDays / 30)} months; or a patrol about ${dollars(patrolMonth)} a month` : `a patrol about ${dollars(patrolMonth)} a month`) : undefined,
    gives: target ? (liaisonFree ? 'while your liaison consults, no island\'s volunteers can be set up' : liaisonWaitWords(liaisonBusy(world) ?? { doing: 'is at work', until: epochDayOf(world), count: 1 })) : undefined,
    others: liaisonFree ? 'A protected area takes your liaison months with its community and lasts; a focused patrol starts at once and lasts its term, paid by the day.'
      : noLiaison ? 'The conservancy employs no liaison: a focused patrol starts at once, paid by the day; a protected area waits for one hired.'
      : 'Your liaison is busy: a focused patrol starts at once, paid by the day; a protected area waits for the liaison.',
  };
}

/** The island with the most of its reef in the shallows, the heat's most exposed (`concernsOf`): the season's count's. */
export const mostExposed = (world: World): ReefIsland => REEF_ISLANDS.find((island) => concernsOf(world, island)?.mostExposed) ?? 'maui';

function season(world: World): Goal {
  const today = epochDayOf(world);
  const civil = civilFromEpochDay(today);
  const s = world.sanctuaries[0];
  const ops = world.operations;
  const exposed = mostExposed(world);
  const assessment = surveyCost('reefBleaching', ECONOMY.assessmentDays);
  const from = epochDayFromCivil(civil.year, SEASON.fromMonth, 1);
  const end = epochDayFromCivil(civil.year, SEASON.endMonth, SEASON.endDay);
  const others = `A coral count before July is the baseline the season's toll is read against; money for a bleaching check, ${dollars(assessment)}, keeps the choice open${ops.boats < 2 ? '; a second boat assesses while the first keeps the plan' : ''}.`;
  // An episode of heat at NOAA's first alert, whatever the date (review M278's: one running past 31 October dropped its
  // choice): its choice asked first, or told made.
  if ((s?.heatStress.peakDhw ?? 0) >= HEAT_ALERT_DHW) {
    if (ops.heatChoice === null) return { id: 'season', title: 'Meet the heat', state: 'Heat stress has reached NOAA\'s first alert: choose what you will know of its toll.', level: 'open', way: { label: 'The heat\'s choice', act: 'overview' }, others };
    return { id: 'season', title: 'Meet the heat', state: ops.heatChoice === 'assess' ? 'The heat came: its bleaching check chosen.' : 'The heat came: the plan kept, its toll for the next coral survey.', level: 'met', way: null, others };
  }
  if (today >= from && today <= end) return { id: 'season', title: 'Meet the warm season', state: `The warm season, to ${dateLabel(end)}: no alert yet.`, level: 'doing', way: null, others };
  // Before the season, or after it for the next: a baseline since the last season ended (review M278's: judged by the
  // calendar year, a count after one season was dropped on 1 January), on the island with the most shallow reef, and
  // the assessment's cost in hand.
  const year = today > end ? civil.year + 1 : civil.year;
  const since = (today > end ? end : epochDayFromCivil(civil.year - 1, SEASON.endMonth, SEASON.endDay)) + 1;
  const baseline = ops.areaCounts.some((c) => c.island === exposed && c.kind === 'cover' && c.by === 'conservancy' && c.day >= since);
  const going = !baseline && underway(world, exposed, ['reefBenthic']);
  const money = ops.funds >= assessment;
  return {
    id: 'season', title: `Prepare for the warm season of ${year}`,
    state: `${baseline ? `${ISLAND_NAMES[exposed]}'s coral counted since the last season` : going ? `${ISLAND_NAMES[exposed]}'s coral survey under way` : `${ISLAND_NAMES[exposed]}'s coral, the most of its reef in the shallows, not counted since the last season`}; ${money ? 'an assessment\'s cost in hand' : `less than an assessment's ${dollars(assessment)} in hand`}.`,
    level: baseline && money ? 'met' : baseline || money || going ? 'doing' : 'open',
    way: !baseline && !going ? { label: `Count ${ISLAND_NAMES[exposed]}'s coral`, act: `area:${exposed}:reefBenthic` } : !money ? { label: 'The accounts', act: 'operations' } : null,
    cost: !baseline && !going ? surveyWords('reefBenthic') : undefined,
    gives: !baseline && !going ? boatTradeoff(world) : undefined,
    others,
  };
}

function money(world: World): Goal {
  const ops = world.operations;
  const months = monthsInHand(world);
  // The funds' own levels (`fundsLevel`): sound at six months or more, under way at two.
  const level = ops.funds >= 0 && months >= FUNDS_MONTHS.good ? 'met' : ops.funds >= 0 && months >= FUNDS_MONTHS.fair ? 'doing' : 'open';
  return {
    id: 'money', title: 'Keep the program solvent',
    state: ops.funds < 0 ? `In debt, ${dollars(ops.funds)}: patrols lapse and surveys wait.` : Number.isFinite(months) ? `${dollars(ops.funds)} in hand, ${runwayWords(world)}.` : `${dollars(ops.funds)} in hand.`,
    level,
    way: level === 'met' ? null : { label: 'The accounts', act: 'operations' },
    others: `Each survey home brings a research grant of ${dollars(ECONOMY.researchGrantPerSurvey)}; the tourism partners pay by the coral you know; patrols, volunteers and staff cost by the day, and the State's grant comes each 1 July.`,
  };
}

/**
 * A shore cleanup's days as the goal's way sends it (M309, the field loop's F8, a game's choice: the shortest trip the
 * plan offers, `ECONOMY.surveyDays`' first, so a cleanup sent after the first survey is home within the first month).
 */
export const SHORE_DAYS = 5;

/**
 * Derelict nets cleared from a shore (M309, the field loop's F8, `docs/FIELD_LOOP_ROADMAP.md` "The loop's design"): the
 * reef island whose shore holds the most tonnes today (`shoreDebrisT`, ties in `REEF_ISLANDS`' order), its way that
 * island's cleanup at `SHORE_DAYS`. Waits while any cleanup is planned or at sea; met only while no shore holds a trip's
 * clearing, the gear coming ashore all year (M307) and the goal open again as it does. The last goal, so the surveys and the protection lead and the mission fills the boat's idle days.
 */
function shore(world: World): Goal {
  const ops = world.operations;
  const tonnes = REEF_ISLANDS.map((island) => ({ island, t: shoreDebrisT(world, island) }));
  const most = tonnes.reduce((a, b) => (b.t > a.t ? b : a));
  const name = ISLAND_NAMES[most.island];
  const sea = atSea(ops).find((j) => j.survey === 'shoreCleanup');
  const planned = ops.plan.find((p) => p.kind === 'shoreCleanup');
  const going = sea?.island ?? planned?.island ?? null;
  const last = ops.finished.filter((f) => isCleanupHome(f) && f.island !== null).at(-1);
  const lies = `About ${Math.round(most.t)} t of derelict nets lie on ${name}'s shore`;
  const rate = CLEARED_T_PER_DAY === 0.5 ? 'half a tonne' : `${CLEARED_T_PER_DAY} t`;
  // A trip offered only where it fills its days (its check, M309: the pacing's player had sent 60 a year, the late ones
  // clearing a few tenths of a tonne): no shore holding a trip's clearing, the goal is met until the nets come ashore
  // again (a game choice, M178; the shores' cleanups repeat "every three years or so" at Kanapou, M307).
  const trip = CLEARED_T_PER_DAY * SHORE_DAYS;
  if (!going && most.t < trip) {
    return { id: 'shore', title: 'Clear derelict nets from a shore', state: `Every shore cleared to under ${trip.toFixed(1)} t, a trip's clearing: nets come ashore again all year.`, level: 'met', way: null, others: 'Each shore is its own trip, on the boat the surveys use.' };
  }
  return {
    id: 'shore', title: 'Clear derelict nets from a shore',
    state: going ? `${ISLAND_NAMES[going]}'s shore cleanup ${sea ? 'is at sea' : 'waits in the plan'}. ${lies}.`
      : last ? `${lies}. The last cleanup: ${(last.tonnes ?? 0).toFixed(1)} t removed from ${ISLAND_NAMES[last.island as ReefIsland]}'s shore on ${dateLabel(last.endDay)}.`
      : `${lies}; the boat clears ${rate} a day.`,
    level: going ? 'doing' : 'open',
    way: going ? null : { label: `Clear ${name}'s shore`, act: `area:${most.island}:shoreCleanup` },
    others: 'Each shore is its own trip, on the boat the surveys use. Nets come ashore all year, so no shore stays clear.',
    cost: going ? undefined : `${dollars(surveyCost('shoreCleanup', SHORE_DAYS))}, a boat ${SHORE_DAYS} days`,
    gives: going ? undefined : boatTradeoff(world),
  };
}

/** An island's steps in their order (M292; its shore's cleanup first where its nets lead, M316): a later one taken is a step done. */
export const ISLAND_STEPS = ['shore', 'coral', 'protect', 'count', 'none'] as const;

/** What leads an island's card (M316, the opening's G6): the problem where it stands first of the four, or what it still holds. */
export type IslandLeadKind = 'fall' | 'nets' | 'heat' | 'holds';
export interface IslandLead {
  island: ReefIsland;
  kind: IslandLeadKind;
  /** The lead in a sentence, every figure the world's own. */
  words: string;
}

/** An island's coral's fall on NOAA's record, points of live cover (the goals' own measure: `coralVerdict`'s, past its error), none where it did not fall. */
const fallOf = (world: World, island: ReefIsland): number => {
  const v = coralVerdict(world, island, true);
  return v.trend === 'down' && v.against && v.last ? v.against.value - v.last.value : 0;
};

/**
 * Each island's lead (M316, `docs/OPENING_LOOP_ROADMAP.md`'s G6, the fourth playtest's: "Give each island a distinct
 * problem so moving islands feels like a new challenge"), read from the world each day. Three problems the game
 * simulates and the player can act on, each led by the one island first of the four in it:
 * - **fall**: the largest fall of its live coral on NOAA's record (`coralVerdict`, past its error; the goals' `decline`);
 * - **nets**: the most derelict nets on its shore (`shoreDebrisT`), where they hold a trip's clearing (the shore goal's
 *   floor, `CLEARED_T_PER_DAY` over `SHORE_DAYS`), so its cleanup can be offered;
 * - **heat**: the most of its reef in the shallows, where a heatwave's heat is least softened by depth (`concernsOf`'s
 *   `mostExposed`).
 * An island first in two takes the one first in that order (a game's choice, M178: the coral's own measured loss before
 * a harm one trip answers, and that before a standing exposure only the warm season brings); the other goes unled, not
 * passed to the next island, so every lead's "the most of the four" stays true. An exact tie goes to the first in
 * `REEF_ISLANDS`' order, as the shore goal's. An island first in none is led by what it still holds: its live coral (the
 * card's own figure, `coralVerdict` with the conservancy's counts), "the most of the four" where it is, with no fall;
 * or, where that record shows one, its fall. Each island one lead; at a new game (seed 42) the four distinct: Maui's fall,
 * Lānaʻi's nets, Molokaʻi's shallows, Kahoʻolawe's coral held.
 */
export function islandLeads(world: World): Record<ReefIsland, IslandLead> {
  const trip = CLEARED_T_PER_DAY * SHORE_DAYS;
  const firstBy = (score: (island: ReefIsland) => number, holds: (island: ReefIsland) => boolean): ReefIsland | null =>
    REEF_ISLANDS.filter(holds).reduce<ReefIsland | null>((a, b) => (a === null || score(b) > score(a) ? b : a), null);
  const falls = Object.fromEntries(REEF_ISLANDS.map((island) => [island, fallOf(world, island)])) as Record<ReefIsland, number>;
  const nets = Object.fromEntries(REEF_ISLANDS.map((island) => [island, shoreDebrisT(world, island)])) as Record<ReefIsland, number>;
  const firsts: readonly [IslandLeadKind, ReefIsland | null][] = [
    ['fall', firstBy((i) => falls[i], (i) => falls[i] > 0)],
    ['nets', firstBy((i) => nets[i], (i) => nets[i] >= trip)],
    ['heat', REEF_ISLANDS.find((i) => concernsOf(world, i)?.mostExposed) ?? null],
  ];
  const live = Object.fromEntries(REEF_ISLANDS.map((island) => [island, coralVerdict(world, island).last?.value ?? null])) as Record<ReefIsland, number | null>;
  const mostLive = firstBy((i) => live[i] as number, (i) => live[i] !== null);
  // Each in one row of a phone's card (M313's room: the fourth card's button at 583 px of 600, MEASURED with
  // `npm run review:cards`); the island named by the card's own heading above it.
  const lead = (island: ReefIsland): IslandLead => {
    const kind = firsts.find(([, first]) => first === island)?.[0] ?? 'holds';
    const v = coralVerdict(world, island, true);
    // Its end the latest count, the player's own too (its review: the card had told NOAA's 19% above the player's 16%).
    const latest = coralVerdict(world, island).last ?? v.last;
    if (kind === 'fall' && v.against && latest) return { island, kind, words: `Coral fell furthest of the four: ${v.against.value.toFixed(0)}% to ${latest.value.toFixed(0)}% since ${v.against.year}.` };
    if (kind === 'nets') return { island, kind, words: `The most derelict nets on any shore: about ${Math.round(nets[island])} t.` };
    if (kind === 'heat') return { island, kind, words: `The most reef in the shallows, ${Math.round(100 * (concernsOf(world, island)?.shallowShare ?? 0))}%: the heat's first.` };
    const held = live[island];
    if (held === null) return { island, kind, words: 'Its coral never measured.' };
    // Its own fall, if any, as the card's figure reads it (every count, the conservancy's too): never "no fall" told of
    // a reef the player's own survey found fallen.
    const own = coralVerdict(world, island);
    if (own.trend === 'down' && own.against && own.last) return { island, kind, words: `Coral fell from ${own.against.value.toFixed(0)}% to ${own.last.value.toFixed(0)}% since ${own.against.year}.` };
    // NOAA's record's fall told too (its review: a count past its error above the record's low had read "no fall").
    if (v.trend === 'down' && v.against && own.last) return { island, kind, words: `Coral fell from ${v.against.value.toFixed(0)}% to ${own.last.value.toFixed(0)}% since ${v.against.year}.` };
    return { island, kind, words: mostLive === island ? `The most live coral of the four, ${held.toFixed(0)}%, and no fall.` : `No fall on the record: ${held.toFixed(0)}% live coral on its reef.` };
  };
  return Object.fromEntries(REEF_ISLANDS.map((island) => [island, lead(island)])) as Record<ReefIsland, IslandLead>;
}

/** One island's lead (M316): `islandLeads`' for that island. */
export const islandProblem = (world: World, island: ReefIsland): IslandLead => islandLeads(world)[island];

/**
 * Whether one more island's patrol leaves the funds half a year of running costs (M317, G7: the first session's probe
 * found the guidance buying a patrol on every island in one day, $85,113 a month, two months in hand). GAME CHOICE: the
 * funds' own "good" line (`FUNDS_MONTHS.good`) after the patrol's month is added; below it the patrol is still on the
 * island's page, not recommended. At the opening's $360,000 one patrol leaves 7 months, a second 5.
 */
export function patrolAffordable(world: World, island: ReefIsland): boolean {
  const added = patrolHoursAdded(island, 1) * ECONOMY.officerHour * MONTH_DAYS / 365;
  return world.operations.funds >= 0 && world.operations.funds / (recurringPerMonth(world).total + added) >= FUNDS_MONTHS.good && patrolFitsYear(world, island);
}

/**
 * Whether the patrol the guidance opens (the Protect page's, its last term, `ECONOMY.patrolTerms`) fits the year's money
 * to the next grant, as that page reckons it (M324's review: put while the funds' row read short; its second review:
 * reckoned to the grant, a year, the page's patrol 90 days).
 */
function patrolFitsYear(world: World, island: ReefIsland): boolean {
  const today = epochDayOf(world);
  const days = Math.min(nextGrantDay(today), today + (ECONOMY.patrolTerms.at(-1) as number)) - today;
  return yearMoney(world).spare >= (patrolHoursAdded(island, 1) * ECONOMY.officerHour * days) / 365;
}

/** Whether the boats' plan holds room for one more trip (M317, G7: the card offered a count the core refused, "the plan holds 4 surveys already"). */
export const planRoom = (world: World): boolean => world.operations.plan.length < ECONOMY.planLength;

/** The words for a trip the full plan holds back (M317). */
const planFullWords = (): string => ` The boats' plan holds ${ECONOMY.planLength} trips already: this one waits for room.`;

/** One island's next step (M292, the one-island roadmap's R2): what to do there now, or that nothing waits there. */
export interface IslandStep {
  island: ReefIsland;
  /** A short key for the step, so the card can tell a step done from an island changed. */
  key: (typeof ISLAND_STEPS)[number];
  title: string;
  state: string;
  way: { label: string; act: string } | null;
  cost?: string;
}

/**
 * An island's next step (M292, the roadmap's R2, the playtest's "Selecting an island should align its status, actions,
 * projects, and recommendations"), by the goals' own rules read on that island alone, in their order: its coral counted
 * where NOAA's record shows it fell and nothing has measured it within the year (`decline`); its grazing fish protected
 * or patrolled (`pressure`); once they are, their count within the year (`evaluate`). A step under way (a survey at sea,
 * a proposal in consultation, volunteers training) is no step to take: the next is offered while it runs. None left:
 * nothing waits on this island.
 */
export function islandStep(world: World, island: ReefIsland): IslandStep {
  const i = REEF_ISLANDS.indexOf(island);
  const name = ISLAND_NAMES[island];
  const m = world.sanctuaries[0]?.management;
  const ops = world.operations;
  const fell = coralVerdict(world, island, true).trend === 'down';
  const coralGoing = underway(world, island, ['reefBenthic']);
  const coralDue = fell && !countedLately(world, island, 'cover') && !coralGoing;
  // An island led by its nets offers its cleanup before its coral survey (M316, G6: the guidance's next island a different
  // kind of work, not the same survey again), again each time one is home while its coral waits; while one is at sea or
  // planned, its coral survey is the step.
  // One trip of its own beyond the guide's first (its review: each cleanup home had asked another, six before its coral),
  // and none while any cleanup is planned or at sea (the shore goal's own wait).
  const homes = ops.finished.filter((f) => isCleanupHome(f) && f.island === island).length;
  const guides = ops.finished.find((f) => isCleanupHome(f))?.island === island ? 1 : 0;
  const anyGoing = ops.jobs.some((j) => j.survey === 'shoreCleanup') || ops.plan.some((p) => p.kind === 'shoreCleanup');
  const room = planRoom(world);
  if (coralDue && islandProblem(world, island).kind === 'nets' && homes < 1 + guides && !anyGoing) {
    const t = Math.round(shoreDebrisT(world, island));
    return { island, key: 'shore', title: `Clear ${name}'s shore`, state: `About ${t} t of derelict nets lie on ${name}'s shore, the most on any; a trip clears ${(CLEARED_T_PER_DAY * SHORE_DAYS).toFixed(1)} t, and its coral's survey follows.${room ? '' : planFullWords()}`, way: room ? { label: `Clear ${name}'s shore`, act: `area:${island}:shoreCleanup` } : null, cost: `${dollars(surveyCost('shoreCleanup', SHORE_DAYS))}, a boat ${SHORE_DAYS} days` };
  }
  if (coralDue) {
    const state = `${fellWords(world, island)}.${room ? '' : planFullWords()}`;
    return { island, key: 'coral', title: `Survey ${name}'s coral`, state, way: room ? { label: `Survey ${name}'s coral`, act: `area:${island}:reefBenthic` } : null, cost: surveyWords('reefBenthic') };
  }
  const inForce = m?.herbivoreProtection[i] === true;
  const patrolled = (m?.patrols[i] ?? 0) > 0;
  const proposed = protectionJob(ops, island) !== undefined;
  if (!inForce && !patrolled && !proposed) {
    // No liaison free: the patrol offered, the protected area's wait told (M311).
    const busy = liaisonBusy(world);
    // A patrol only while the funds keep half a year in hand after it (M317, G7).
    if (busy) {
      const can = patrolAffordable(world, island);
      return { island, key: 'protect', title: `Protect ${name}'s grazing fish`, state: `${name}'s grazing fish are protected only by Hawaiʻi's own rules; ${liaisonWaitWords(busy)}.${can ? '' : patrolFitsYear(world, island) ? ` A patrol here would leave the funds under ${FUNDS_MONTHS.good} months of running costs: it waits for money.` : ' A patrol here would cost more than is spare to the next grant: it waits for money.'}`, way: can ? { label: `Patrol ${name}'s reef now`, act: `respond:${island}` } : null, cost: can ? 'a patrol from tomorrow, paid by the day' : undefined };
    }
    return { island, key: 'protect', title: `Protect ${name}'s grazing fish`, state: `${name}'s grazing fish are protected only by Hawaiʻi's own rules.`, way: { label: `Protect ${name}'s grazing fish`, act: `respond:${island}` }, cost: `a protected area ${dollars(ECONOMY.rule.consultationCost)} and your liaison ${Math.round(ECONOMY.rule.consultationDays / 30)} months; or a patrol from tomorrow` };
  }
  const volunteers = volunteersOn(ops, island);
  if ((inForce || patrolled) && !countedLately(world, island, 'herbivores') && !underway(world, island, ['reefFish']) && !volunteers) {
    return { island, key: 'count', title: `Count ${name}'s grazing fish`, state: `${name}'s grazing fish are ${inForce ? 'protected' : 'patrolled'} but not counted within the year: without a count, no one can tell whether it works.${room ? '' : planFullWords()}`, way: room ? { label: `Count ${name}'s grazing fish`, act: `area:${island}:reefFish` } : null, cost: surveyWords('reefFish') };
  }
  // Nothing to take: what runs here, in a line.
  const runs = [coralGoing ? 'a coral survey under way' : '', proposed ? 'a protected area in consultation' : inForce ? 'a protected area in force' : '', patrolled ? 'a patrol on the water' : '', volunteers ? 'volunteers counting' : ''].filter(Boolean);
  return { island, key: 'none', title: `${name}: nothing waits`, state: runs.length > 0 ? `${name}: ${runs.join(', ')}.` : `${name}'s reef asks nothing of you now.`, way: null };
}

/**
 * An island's fall in a sentence, unstopped: your own last count where there is one, past its year, in NOAA's place
 * (review 33: the card quoted NOAA's 2024 while the island's line beside it showed the conservancy's newer count; the
 * sanctuary's goal and the island's step one wording, its reviewer's).
 */
function fellWords(world: World, island: ReefIsland): string {
  const name = ISLAND_NAMES[island];
  const ours = coralHistory(world, island).filter((p) => p.by === 'conservancy').at(-1);
  if (ours) return `${name}'s coral was ${ours.value.toFixed(0)}% when you counted it in ${ours.year}, more than a year ago; it had fallen on NOAA's record`;
  const v = coralVerdict(world, island, true);
  return v.against && v.last ? `${name}'s coral fell from ${v.against.value.toFixed(0)}% (${v.against.year}) to ${v.last.value.toFixed(0)}% (${v.last.year}); whether it is still falling is unknown` : `${name}'s coral fell on NOAA's record`;
}

/** The next island with a step to take after this one (M292): the islands in the record's order of concern, this one last. */
export function nextIsland(world: World, after: ReefIsland): ReefIsland | null {
  const fall = (island: ReefIsland): number => { const v = coralVerdict(world, island, true); return v.trend === 'down' && v.against && v.last ? v.against.value - v.last.value : 0; };
  return REEF_ISLANDS.filter((island) => island !== after).sort((a, b) => fall(b) - fall(a)).find((island) => islandStep(world, island).way !== null) ?? null;
}

/**
 * The sanctuary's goals, the brief's four in its order, then the shore's mission last (M309); none where it holds no reef.
 * The shore's first where the coral's next island is led by its nets and its step is its cleanup (M316, G6, the fourth
 * playtest's: "After starting protection, the main guidance sent me to survey Lānaʻi"): the guidance follows the next
 * island's lead, the coral's survey there after it.
 */
export function goalsOf(world: World): Goal[] {
  const s = world.sanctuaries[0];
  if (!s?.coral || !s.fish) return [];
  const goals = [decline(world), pressure(world), evaluate(world), season(world), money(world), shore(world)].filter((g): g is Goal => g !== null);
  const next = goals[0]?.way?.act.split(':')[1] as ReefIsland | undefined;
  const clear = goals.find((g) => g.id === 'shore');
  const ordered = next && clear?.way && clear.way.act === `area:${next}:shoreCleanup` && islandStep(world, next).key === 'shore' ? [clear, ...goals.filter((g) => g !== clear)] : goals;
  // A boat trip recommended only while the plan has room (M317's review: the home card, no island chosen, still offered
  // the trip the core refused, "the plan holds 4 surveys already"); the goal stands, its way waits.
  if (planRoom(world)) return ordered;
  return ordered.map((g) => (g.way && /^area:[^:]+:[^:]+$/.test(g.way.act) ? { ...g, way: null, state: `${g.state}${planFullWords()}` } : g));
}

/** Whether the first chapter's guidance is still under way: the goals then wait behind it (review M278's: two "do this now"s at once). */
export const inTutorial = (world: World): boolean => currentObjective(world)?.chapter === 'firstSurvey';

/**
 * The goal recommended now: the one that cannot wait first (the heat's choice, money short), else the first not met
 * with a way to it now; none where every goal is met or waits.
 */
export function recommendedGoal(goals: readonly Goal[]): Goal | null {
  const urgent = goals.find((g) => g.level === 'open' && g.way && (g.id === 'season' && g.way.act === 'overview' || g.id === 'money'));
  return urgent ?? goals.find((g) => g.level !== 'met' && g.way) ?? null;
}
